import { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    Autocomplete,
    TextField,
} from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { URLHandler } from "src/services/URLHandler";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

interface ProductWithChain {
    id: number;
    title: string;
    isIntermediate: boolean;
    ownedBy?: {
        name: string;
    };
    chainMetadata?: {
        upstreamCount: number;
        downstreamCount: number;
        totalEntities: number;
        maxDepth: number;
    };
}

const getComplexityLevel = (totalEntities: number): "simple" | "medium" | "complex" => {
    if (totalEntities <= 5) return "simple";
    if (totalEntities <= 15) return "medium";
    return "complex";
};

const getComplexityColor = (level: "simple" | "medium" | "complex") => {
    switch (level) {
        case "simple":
            return "success";
        case "medium":
            return "warning";
        case "complex":
            return "error";
        default:
            return "default";
    }
};

export const ValueChainList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { selectedCompany } = useFoodpilotContext();
    const [selectedProduct, setSelectedProduct] = useState<ProductWithChain | null>(null);
    const { delayedSearch, setSearch } = useDelayedSearch();

    // Fetch ALL products (intermediate and regular products with ingredients)
    const { data: products = [], isLoading } = useQuery({
        queryKey: ["value-chain-products", selectedCompany?.id, delayedSearch],
        queryFn: async () => {
            if (!selectedCompany) return [];

            const response = await loggedInApiCall.get("/api/products", {
                params: {
                    title: delayedSearch,
                    itemsPerPage: 100,
                    "order[title]": "asc",
                    // Don't filter by isIntermediate - get ALL products
                },
            });

            return response.data["hydra:member"] || [];
        },
        enabled: !!selectedCompany,
        staleTime: 30000,
    });

    // Filter and map to ProductWithChain type
    // Show products that are intermediate OR have ingredients (have a value chain)
    const productsWithChains: ProductWithChain[] = products
        .filter((p: any) => {
            // Include if it's an intermediate product
            if (p.isIntermediate) return true;
            // Include if it has ingredients (has a chain)
            if (p.productIngredients && p.productIngredients.length > 0) return true;
            return false;
        })
        .map((p: any) => ({
            id: p.id,
            title: p.title,
            isIntermediate: p.isIntermediate || false,
            ownedBy: p.ownedBy,
            chainMetadata: undefined,
        }));

    const handleProductClick = (product: ProductWithChain) => {
        if (!selectedCompany) return;
        const url = URLHandler.product.valueChain(selectedCompany.id, product.id);
        navigate(url);
    };

    if (!selectedCompany) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="warning">{t("please_select_company", "Veuillez sélectionner une entreprise")}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Stack spacing={3}>
                {/* Header */}
                <Box>
                    <Typography variant="h1" gutterBottom>
                        {t("value_chain.list.title", "Value chains")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t(
                            "value_chain.list.description",
                            "View and explore the value chains of your products. Click on a product to see its upstream and downstream traceability.",
                        )}
                    </Typography>
                </Box>

                {/* Product Search/Filter */}
                <Autocomplete
                    fullWidth
                    options={productsWithChains}
                    value={selectedProduct}
                    onChange={(_, newValue) => {
                        setSelectedProduct(newValue);
                        if (newValue) {
                            handleProductClick(newValue);
                        }
                    }}
                    onInputChange={(_, newInputValue) => {
                        setSearch(newInputValue);
                    }}
                    getOptionLabel={(option) => {
                        let label = option.title;
                        if (option.ownedBy?.name) {
                            label += ` - ${option.ownedBy.name}`;
                        }
                        return label;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    loading={isLoading}
                    filterOptions={(options) => options}
                    noOptionsText={
                        isLoading ? t("common.loading", "Chargement...")
                        : delayedSearch ?
                            t("common.noResults", "Aucun résultat")
                        :   t("value_chain.list.type_to_search", "Type to search for a product...")
                    }
                    loadingText={t("common.loading", "Chargement...")}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={t("value_chain.list.search_placeholder", "Search for a product...")}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoading ?
                                            <CircularProgress color="inherit" size={20} />
                                        :   null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                                <FoodsIcon icon="products" size={2} />
                                <Stack flexGrow={1}>
                                    <Typography variant="body2">{option.title}</Typography>
                                    {option.ownedBy && (
                                        <Typography variant="caption" color="text.secondary">
                                            {option.ownedBy.name}
                                        </Typography>
                                    )}
                                </Stack>
                                {option.isIntermediate && (
                                    <Chip label={t("intermediate", "Intermediate")} size="small" color="primary" />
                                )}
                            </Stack>
                        </li>
                    )}
                />

                {/* Loading */}
                {isLoading && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Empty state */}
                {!isLoading && productsWithChains && productsWithChains.length === 0 && (
                    <Alert severity="info">
                        {delayedSearch ?
                            t("no_products_found", "Aucun produit trouvé")
                        :   t(
                                "value_chain.list.no_products",
                                "Aucun produit avec une chaîne de valeur trouvé. Les produits doivent être des produits intermédiaires ou avoir des ingrédients pour avoir une chaîne de valeur.",
                            )
                        }
                    </Alert>
                )}

                {/* Product Grid */}
                {!isLoading && productsWithChains && productsWithChains.length > 0 && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            {t("value_chain.list.count", "{{count}} product(s) found", {
                                count: productsWithChains.length,
                            })}
                        </Typography>

                        <Grid container spacing={3}>
                            {productsWithChains.map((product) => {
                                const complexity = getComplexityLevel(product.chainMetadata?.totalEntities || 0);

                                return (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                                        <Card
                                            sx={{
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                transition: "all 0.2s",
                                                "&:hover": {
                                                    boxShadow: 4,
                                                    transform: "translateY(-4px)",
                                                },
                                            }}
                                        >
                                            <CardActionArea
                                                onClick={() => handleProductClick(product)}
                                                sx={{ height: "100%", display: "flex", alignItems: "flex-start" }}
                                            >
                                                <CardContent sx={{ width: "100%", flexGrow: 1 }}>
                                                    <Stack spacing={2}>
                                                        {/* Icon and Type */}
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <FoodsIcon icon="products" size={3} />
                                                            {product.isIntermediate && (
                                                                <Chip
                                                                    label={t("intermediate", "Intermediate")}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                                                />
                                                            )}
                                                        </Stack>

                                                        {/* Title */}
                                                        <Typography
                                                            variant="h6"
                                                            component="div"
                                                            sx={{
                                                                fontSize: "1rem",
                                                                fontWeight: 600,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                minHeight: "3em",
                                                            }}
                                                        >
                                                            {product.title}
                                                        </Typography>

                                                        {/* Owner */}
                                                        {product.ownedBy && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {product.ownedBy.name}
                                                            </Typography>
                                                        )}

                                                        {/* Chain Stats */}
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                                                            <Chip
                                                                label={t("value_chain.has_chain", "Chain available")}
                                                                size="small"
                                                                color="info"
                                                                variant="outlined"
                                                                sx={{ height: 24, fontSize: "0.75rem" }}
                                                            />
                                                            {product.chainMetadata && (
                                                                <Chip
                                                                    label={`${product.chainMetadata.totalEntities || "?"} ${t("value_chain.entities", "entities")}`}
                                                                    size="small"
                                                                    color={getComplexityColor(complexity)}
                                                                    sx={{ height: 24, fontSize: "0.75rem" }}
                                                                />
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </>
                )}
            </Stack>
        </Container>
    );
};
